"use client";

import * as React from "react";
import {
  getDefaultOverlayPortal,
  nestedSurfaceHostFor,
} from "./overlay-portal";

/** Stack top as seen in the render that opened the surface. */
interface OverlayHostSnapshot {
  readonly open: boolean;
  readonly el: HTMLElement | null;
}

/**
 * The innermost registered host branch (fullscreen task popup, file preview,
 * merge shell, …) at the moment a surface opens — held for as long as `open`
 * stays true, `null` while closed or when nothing is registered.
 *
 * Read once on the open transition on purpose. Re-reading on every render is
 * a trap for any surface that registers its own branch on that stack: from
 * the render after its own push it would resolve *itself* as host, React
 * would move the portal into that branch — a node inside the content it just
 * removed — and the surface would be gone without a trace.
 */
export function useOverlayHostAtOpen(open: boolean): HTMLElement | null {
  const [snapshot, setSnapshot] = React.useState<OverlayHostSnapshot>(
    (): OverlayHostSnapshot => ({
      open,
      el: open ? nestedSurfaceHostFor(getDefaultOverlayPortal()) : null,
    }),
  );
  let el: HTMLElement | null = snapshot.el;
  if (snapshot.open !== open) {
    // Derived state from the `open` transition — React re-renders at once.
    el = open ? nestedSurfaceHostFor(getDefaultOverlayPortal()) : null;
    setSnapshot({ open, el });
  }
  return el;
}

/**
 * Host branch the dialog's content mounts into; `null` = `document.body`.
 * Set by `Dialog` / `Sheet`, read by their `*Content`.
 */
export const DialogHostContext: React.Context<HTMLElement | null> =
  React.createContext<HTMLElement | null>(null);

/**
 * Surface mounted inside a host branch: above the host's own chrome
 * (`z-[1]`) and its pickers (`z-[100]`), clickable inside the host's
 * `pointer-events-none` branch.
 */
export const NESTED_IN_HOST_SURFACE_CLASS: string =
  "z-[110] pointer-events-auto";
